import React, { useCallback, useEffect, useState } from "react";
import { Card, Col, DatePicker, Row, Segmented, Table } from "antd";
import dayjs from "dayjs";
import adminApi from "../../../services/adminApi";
import ReportShell from "../ReportShell";
import {
  DateRange,
  DEFAULT_RANGE,
  RANGE_PRESETS,
  rangeLabel,
  rangeParams,
} from "../reportCommon";
import {
  ExportColumn,
  exportCsv,
  exportPdf,
  formatCurrency,
  pdfCurrency,
  PharmacyHeader,
} from "../../../utils/reportExport";

const { RangePicker } = DatePicker;

type GroupBy = "day" | "week" | "month";

interface Props {
  pharmacy: PharmacyHeader;
}

const periodLabel = (id: any, groupBy: GroupBy): string => {
  if (!id) return "—";
  if (groupBy === "month") {
    return dayjs(`${id.year}-${String(id.month).padStart(2, "0")}-01`).format(
      "MMM YYYY"
    );
  }
  if (groupBy === "week") return `Wk ${id.week}, ${id.year}`;
  return dayjs(
    `${id.year}-${String(id.month).padStart(2, "0")}-${String(id.day).padStart(2, "0")}`
  ).format("DD/MM/YYYY");
};

const SalesTrendReport: React.FC<Props> = ({ pharmacy }) => {
  const [range, setRange] = useState<DateRange>(DEFAULT_RANGE);
  const [groupBy, setGroupBy] = useState<GroupBy>("day");
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.getSalesTrendReport({
        ...rangeParams(range),
        groupBy,
      });
      setData(res.data.data);
    } catch (e: any) {
      setError(e.response?.data?.message || "Failed to load sales report");
    } finally {
      setLoading(false);
    }
  }, [range, groupBy]);

  useEffect(() => {
    load();
  }, [load]);

  const salesData: any[] = data?.salesData || [];
  const topMedicines: any[] = data?.topMedicines || [];

  const totals = salesData.reduce(
    (acc, r) => ({
      sales: acc.sales + (r.totalSales || 0),
      bills: acc.bills + (r.totalBills || 0),
    }),
    { sales: 0, bills: 0 }
  );
  const avgBill = totals.bills > 0 ? totals.sales / totals.bills : 0;

  const exportColumns: ExportColumn[] = [
    { title: "Period", value: (r) => periodLabel(r._id, groupBy) },
    { title: "Bills", value: (r) => r.totalBills || 0 },
    { title: "Total Sales", value: (r) => (r.totalSales || 0).toFixed(2) },
    { title: "Avg Bill", value: (r) => (r.avgBillAmount || 0).toFixed(2) },
  ];

  const handleExportCsv = () => {
    exportCsv("Sales Trend Report", exportColumns, salesData, [
      `Period: ${rangeLabel(range)} | Grouped by: ${groupBy}`,
      `Total sales: ${totals.sales.toFixed(2)} | Bills: ${totals.bills}`,
    ]);
  };

  const handleExportPdf = () => {
    exportPdf({
      title: "Sales Trend Report",
      pharmacy,
      subtitle: `Period: ${rangeLabel(range)} | Grouped by: ${groupBy}`,
      columns: exportColumns,
      rows: salesData,
      summary: [
        { label: "Total sales", value: pdfCurrency(totals.sales) },
        { label: "Bills", value: String(totals.bills) },
        { label: "Avg bill", value: pdfCurrency(avgBill) },
      ],
    });
  };

  return (
    <ReportShell
      title="Sales Trend"
      loading={loading}
      error={error}
      onRefresh={load}
      onReset={() => {
        setRange(DEFAULT_RANGE);
        setGroupBy("day");
      }}
      onExportCsv={handleExportCsv}
      onExportPdf={handleExportPdf}
      filters={
        <>
          <RangePicker
            size="small"
            value={range}
            allowClear={false}
            presets={RANGE_PRESETS}
            onChange={(v) => v && setRange(v as DateRange)}
          />
          <Segmented
            size="small"
            value={groupBy}
            onChange={(v) => setGroupBy(v as GroupBy)}
            options={[
              { value: "day", label: "Daily" },
              { value: "week", label: "Weekly" },
              { value: "month", label: "Monthly" },
            ]}
          />
        </>
      }
      stats={[
        { title: "Total sales", value: totals.sales, prefix: "₹", precision: 2 },
        { title: "Bills", value: totals.bills },
        { title: "Avg bill", value: avgBill, prefix: "₹", precision: 2 },
      ]}
    >
      <Row gutter={12}>
        <Col xs={24} lg={14}>
          <Table
            rowKey={(r: any) => JSON.stringify(r._id)}
            size="small"
            sticky
            loading={loading}
            dataSource={salesData}
            pagination={{ pageSize: 15, showTotal: (t) => `${t} periods` }}
            columns={[
              {
                title: "Period",
                render: (_: unknown, r: any) => periodLabel(r._id, groupBy),
              },
              {
                title: "Bills",
                dataIndex: "totalBills",
                width: 80,
                align: "right" as const,
              },
              {
                title: "Sales",
                dataIndex: "totalSales",
                width: 120,
                align: "right" as const,
                render: (v: number) => formatCurrency(v),
              },
              {
                title: "Avg bill",
                dataIndex: "avgBillAmount",
                width: 110,
                align: "right" as const,
                render: (v: number) => formatCurrency(v),
              },
            ]}
          />
        </Col>
        <Col xs={24} lg={10}>
          <Card title="Top selling medicines" size="small" loading={loading}>
            <Table
              rowKey={(r: any) => String(r._id)}
              size="small"
              dataSource={topMedicines}
              pagination={false}
              columns={[
                { title: "Medicine", dataIndex: "medicineName", ellipsis: true },
                {
                  title: "Qty",
                  dataIndex: "totalQuantity",
                  width: 70,
                  align: "right" as const,
                },
                {
                  title: "Revenue",
                  dataIndex: "totalRevenue",
                  width: 110,
                  align: "right" as const,
                  render: (v: number) => formatCurrency(v),
                },
              ]}
            />
          </Card>
        </Col>
      </Row>
    </ReportShell>
  );
};

export default SalesTrendReport;
